import React, { useState, useEffect } from "react";
import { Layout, Menu, Table, Switch, Button, Modal, Form, Select, Tag, message } from "antd";
import { UnorderedListOutlined, TagsOutlined, PlusOutlined, DeleteOutlined, SyncOutlined } from "@ant-design/icons";
import { MainLayout } from "layouts";
import styled, { createGlobalStyle } from "styled-components";
import { useCategoryStore } from "store/CategoryStore";
import { getAgentQueues, createAgentQueue, updateAgentQueue, deleteAgentQueue } from "api/agentqueue";
import { postSyncPrivateLabels } from "api/data";

const { Sider, Content } = Layout;

const GlobalAgentStyles = createGlobalStyle`
    .agent-modal-ok-btn,
    .agent-modal-ok-btn:hover,
    .agent-modal-ok-btn:focus,
    .agent-modal-ok-btn:focus-visible,
    .agent-modal-ok-btn:active {
        background: rgba(255, 255, 255, 0.08) !important;
        border: 1px solid rgba(255, 255, 255, 0.15) !important;
        color: rgba(255, 255, 255, 0.85) !important;
        box-shadow: none !important;
        outline: none !important;
    }
    .agent-modal-ok-btn:hover {
        background: rgba(255, 255, 255, 0.14) !important;
        border-color: rgba(255, 255, 255, 0.25) !important;
        color: #fff !important;
    }

    .dark-select-dropdown {
        background: #1c1c1c !important;
        border: 1px solid rgba(255, 255, 255, 0.1);

        .ant-select-item {
            color: rgba(255, 255, 255, 0.85);
        }
        .ant-select-item-option-active,
        .ant-select-item-option:hover {
            background: rgba(255, 255, 255, 0.08) !important;
        }
        .ant-select-item-option-selected {
            background: rgba(255, 255, 255, 0.12) !important;
            color: #fff;
        }
        .ant-select-item-empty {
            color: rgba(255, 255, 255, 0.3);
        }
    }
`;

const SECTIONS = {
    queue: "queue",
    brands: "brands",
    privateLabels: "privateLabels",
};

const QueueView = () => {
    const [queues, setQueues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const { categories, fetchCategories, isLoading: catsLoading } = useCategoryStore();
    const depth4Categories = (Array.isArray(categories) ? categories : []).filter((c) => c.depth === 4);

    useEffect(() => {
        fetchCategories();
        loadQueues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadQueues = async () => {
        setLoading(true);
        const res = await getAgentQueues();
        if (res.success) setQueues(res.payload || []);
        setLoading(false);
    };

    const handleToggleActive = async (record) => {
        const updated = { ...record, active: !record.active };
        const res = await updateAgentQueue(record.id, { active: updated.active });
        if (res.success) {
            setQueues((prev) =>
                prev.map((q) => (q.id === record.id ? { ...q, active: updated.active } : q))
            );
        }
    };

    const handleDeleteQueue = async (record) => {
        Modal.confirm({
            title: "Delete Queue",
            content: `Are you sure you want to delete the queue "${record.name}"?`,
            okText: "Delete",
            okType: "danger",
            onOk: async () => {
                const res = await deleteAgentQueue(record.id);
                if (res.success) {
                    setQueues((prev) => prev.filter((q) => q.id !== record.id));
                }
            },
            styles: {
                content: { background: "#1c1c1c", border: "1px solid rgba(255,255,255,0.1)" },
                header: { background: "#1c1c1c" },
                body: { color: "rgba(255,255,255,0.85)" },
                footer: { background: "#1c1c1c", borderTop: "1px solid rgba(255,255,255,0.1)" },
                mask: { background: "rgba(0,0,0,0.7)" },
            },
        });
    };

    const handleAddQueue = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            const res = await createAgentQueue(values);
            if (res.success) {
                setQueues((prev) => [...prev, res.payload]);
                form.resetFields();
                setModalOpen(false);
            }
        } catch (_) {
            // validation error — do nothing
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: "Category",
            dataIndex: "name",
            key: "name",
            render: (name) => {
                return name ? <span>{name}</span> : <span style={{ color: "rgba(255,255,255,0.3)" }}>—</span>;
            },
        },
        {
            title: "Active",
            dataIndex: "active_f",
            key: "active_f",
            width: 100,
            render: (_, record) => (
                <Switch
                    checked={record.active_f}
                    onChange={() => handleToggleActive(record)}
                    size="small"
                />
            ),
        },
        {
            title: "",
            key: "actions",
            width: 60,
            render: (_, record) => (
                <DeleteButton
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => handleDeleteQueue(record)}
                />
            ),
        },
    ];

    return (
        <div>
            <QueueHeader>
                <SectionTitle>Queue</SectionTitle>
                <AddButton
                    icon={<PlusOutlined />}
                    onClick={() => setModalOpen(true)}
                >
                    Add Queue
                </AddButton>
            </QueueHeader>

            <StyledTable
                dataSource={queues}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20, size: "small" }}
                size="middle"
            />

            <Modal
                title={<ModalTitle>Add New Queue</ModalTitle>}
                open={modalOpen}
                onOk={handleAddQueue}
                onCancel={() => { setModalOpen(false); form.resetFields(); }}
                confirmLoading={saving}
                okText="Create"
                okButtonProps={{ className: "agent-modal-ok-btn" }}
                styles={{
                    content: { background: "#1c1c1c", border: "1px solid rgba(255,255,255,0.1)" },
                    header: { background: "#1c1c1c", borderBottom: "1px solid rgba(255,255,255,0.1)" },
                    footer: { background: "#1c1c1c", borderTop: "1px solid rgba(255,255,255,0.1)" },
                    mask: { background: "rgba(0,0,0,0.7)" },
                }}
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <StyledFormItem
                        name="category_id"
                        label="Category"
                        rules={[{ required: true, message: "Please select a category" }]}
                    >
                        <Select
                            placeholder="Select a category"
                            loading={catsLoading}
                            showSearch
                            optionFilterProp="label"
                            options={depth4Categories.map((c) => ({ value: c.level_i, label: c.level_nm }))}
                            popupClassName="dark-select-dropdown"
                        />
                    </StyledFormItem>

                    <StyledFormItem name="active" label="Active" valuePropName="checked" initialValue={true}>
                        <Switch />
                    </StyledFormItem>
                </Form>
            </Modal>
        </div>
    );
};

const BrandsView = () => (
    <div>
        <SectionTitle>Brands</SectionTitle>
        <SectionDescription>Browse and manage brands here.</SectionDescription>
    </div>
);

const PrivateLabelsView = () => {
    const [syncing, setSyncing] = useState(false);

    const handleSync = async () => {
        setSyncing(true);
        const res = await postSyncPrivateLabels();
        if (res.success) {
            message.success("Private labels synced");
        } else {
            message.error("Failed to sync private labels");
        }
        setSyncing(false);
    };

    return (
        <div>
            <QueueHeader>
                <SectionTitle>Private Labels</SectionTitle>
                <AddButton icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>
                    Sync Private Labels
                </AddButton>
            </QueueHeader>
            <SectionDescription>Pull the latest private label manufacturers for each retailer from PEP.</SectionDescription>
        </div>
    );
};

const menuItems = [
    {
        key: SECTIONS.queue,
        icon: <UnorderedListOutlined />,
        label: "Queue",
    },
    {
        key: SECTIONS.brands,
        icon: <TagsOutlined />,
        label: "Brands",
    },
    {
        key: SECTIONS.privateLabels,
        icon: <SyncOutlined />,
        label: "Private Labels",
    },
];

export const AgentTools = () => {
    const [selected, setSelected] = useState(SECTIONS.queue);

    return (
        <MainLayout>
            <GlobalAgentStyles />
            <StyledLayout>
                <StyledSider width={220}>
                    <SiderTitle>Admin Tools</SiderTitle>
                    <StyledMenu
                        mode="inline"
                        selectedKeys={[selected]}
                        items={menuItems}
                        onClick={({ key }) => setSelected(key)}
                    />
                </StyledSider>
                <StyledContent>
                    {selected === SECTIONS.queue && <QueueView />}
                    {selected === SECTIONS.brands && <BrandsView />}
                    {selected === SECTIONS.privateLabels && <PrivateLabelsView />}
                </StyledContent>
            </StyledLayout>
        </MainLayout>
    );
};

const StyledLayout = styled(Layout)`
    min-height: calc(100vh - 114px);
    background: #131313;
`;

const StyledSider = styled(Sider)`
    background: #131313 !important;
    border-right: 1px solid rgba(255, 255, 255, 0.1);

    .ant-layout-sider-children {
        display: flex;
        flex-direction: column;
    }
`;

const SiderTitle = styled.div`
    padding: 16px 24px;
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 8px;
`;

const StyledMenu = styled(Menu)`
    background: transparent !important;
    border-inline-end: none !important;

    .ant-menu-item {
        color: rgba(255, 255, 255, 0.6);
        border-radius: 6px;
        margin: 2px 8px;
        width: calc(100% - 16px);

        &:hover {
            background: rgba(255, 255, 255, 0.05) !important;
            color: rgba(255, 255, 255, 0.9) !important;
        }

        .ant-menu-item-icon,
        .anticon {
            color: inherit;
        }
    }

    .ant-menu-item-selected {
        background: rgba(255, 255, 255, 0.05) !important;
        color: #ffffff !important;
        border: 1px solid rgba(255, 255, 255, 0.1);
    }
`;

const StyledContent = styled(Content)`
    padding: 24px;
    background: #131313;
`;

const SectionTitle = styled.h2`
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 0;
`;

const SectionDescription = styled.p`
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
`;

const QueueHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const StyledTable = styled(Table)`
    .ant-table {
        background: transparent;
        color: rgba(255, 255, 255, 0.85);
    }
    .ant-table-thead > tr > th {
        background: rgba(255, 255, 255, 0.04);
        color: rgba(255, 255, 255, 0.5);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }
    .ant-table-tbody > tr > td {
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }
    .ant-table-tbody > tr:hover > td {
        background: rgba(255, 255, 255, 0.04) !important;
    }
    .ant-table-tbody > tr > td,
    .ant-table-thead > tr > th {
        background: transparent;
    }
    .ant-pagination {
        .ant-pagination-item a,
        .ant-pagination-prev button,
        .ant-pagination-next button {
            color: rgba(255, 255, 255, 0.5);
        }
        .ant-pagination-item-active {
            background: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.2);
            a { color: #fff; }
        }
    }
`;

const AddButton = styled(Button)`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.85);
    box-shadow: none;

    &:hover,
    &:focus,
    &:focus-visible,
    &:active {
        background: rgba(255, 255, 255, 0.14) !important;
        border-color: rgba(255, 255, 255, 0.25) !important;
        color: #fff !important;
        box-shadow: none !important;
        outline: none !important;
    }
`;

const DeleteButton = styled(Button)`
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.3);
    box-shadow: none;

    &:hover,
    &:focus {
        background: rgba(255, 82, 82, 0.1) !important;
        color: #ff5252 !important;
        border: none !important;
        box-shadow: none !important;
    }
`;

const ModalTitle = styled.span`
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
`;

const StyledFormItem = styled(Form.Item)`
    .ant-form-item-label > label {
        color: rgba(255, 255, 255, 0.6);
        font-size: 13px;
    }
    .ant-input,
    .ant-select-selector {
        background: rgba(255, 255, 255, 0.06) !important;
        border-color: rgba(255, 255, 255, 0.15) !important;
        color: #fff !important;
    }
    .ant-input::placeholder,
    .ant-select-selection-placeholder {
        color: rgba(255, 255, 255, 0.3) !important;
    }
    .ant-select-arrow {
        color: rgba(255, 255, 255, 0.4);
    }
`;

